import { escapeHtml } from '@messenger/utils'
import { caseDetailSpan } from 'utils'

/**
 * Builds a comma-separated string from object fields, filtering out empty values
 * Supports both escaped field values and caseDetailSpan-wrapped values for TinyMCE;
 * either way the result is markup, and a field's value is only ever its text
 *
 * @param {Object} data - The data object containing the fields
 * @param {string[]} fieldKeys - Array of field keys to include
 * @param {Object} options - Configuration options
 * @param {boolean} options.useCaseDetailSpan - Whether to wrap values with caseDetailSpan
 * @param {string} options.keyPrefix - Prefix for caseDetailSpan keys (e.g., "organizations[0]")
 * @returns {string} Comma-separated string of non-empty field values
 *
 * @example
 * // Escaped values (for family members)
 * buildFieldsString(
 *   { name: "John", relation: "Father", city: "", state: "TX" },
 *   ['name', 'relation', 'city', 'state']
 * )
 * // Returns: "John, Father, TX"
 *
 * @example
 * // With caseDetailSpan (for organizations)
 * buildFieldsString(
 *   { name: "Red Cross", city: "Chicago" },
 *   ['name', 'city'],
 *   { useCaseDetailSpan: true, keyPrefix: "organizations[0]" }
 * )
 * // Returns: caseDetailSpan values joined with commas
 */
export const buildFieldsString = (
  data: Record<string, string>,
  fieldKeys: string[],
  options = { useCaseDetailSpan: false, keyPrefix: '' },
) => {
  const validFields: string[] = []

  fieldKeys.forEach((fieldKey) => {
    const fieldHasContent = data?.[fieldKey]?.trim?.()
    if (fieldHasContent) {
      if (options.useCaseDetailSpan) {
        const spanKey = options.keyPrefix
          ? `${options.keyPrefix}.${fieldKey}`
          : fieldKey
        validFields.push(caseDetailSpan(spanKey))
      } else {
        validFields.push(escapeHtml(data[fieldKey]))
      }
    }
  })

  return validFields.join(', ')
}
